/**
 * Quelle consigne d'installation montrer — décision #21.
 *
 * ## Pourquoi un reniflage d'UA est acceptable ICI
 *
 * #14 l'a explicitement écarté pour la redirection entre `app.` et `m.`, et cette
 * décision tient toujours. La différence n'est pas de technique, elle est de
 * **sanction** : là-bas, une erreur de détection envoyait quelqu'un sur la
 * mauvaise app — un blocage. Ici, elle affiche la mauvaise consigne, et chaque
 * écran porte un lien « ce n'est pas ce que je vois » qui déplie les six.
 *
 * Autrement dit le reniflage ne peut enfermer personne. C'est l'échappatoire qui
 * l'autorise, pas la qualité de la détection — et c'est pourquoi elle n'a pas
 * besoin d'être parfaite. Elle choisit un **défaut**, rien de plus.
 *
 * ## Ce qui est délibérément grossier
 *
 * Aucune bibliothèque, aucune table d'UA à maintenir. Les navigateurs mentent
 * dans leur chaîne d'UA depuis vingt ans, et une détection fine échouerait
 * silencieusement à la première version qui change son libellé. Six familles,
 * quelques marqueurs stables, et l'échappatoire pour le reste.
 */

import { t } from '../i18n/langue';

export type ClePlateforme =
    | 'ios-safari'
    | 'ios-other'
    | 'inapp'
    | 'android-chrome'
    | 'android-other'
    | 'desktop';

export interface Consigne {
    cle: ClePlateforme;
    /** Le libellé du sélecteur « ce n'est pas ce que je vois ». */
    libelle: string;
}

/**
 * Les six, dans l'ordre du sélecteur. iPhone d'abord : c'est la plateforme où
 * l'installation est obligatoire (pas de push hors app installée) et la seule où
 * elle ne peut pas être proposée par un bouton.
 */
export const CONSIGNES: readonly Consigne[] = [
    { cle: 'ios-safari', libelle: 'iPhone · Safari' },
    { cle: 'ios-other', libelle: 'iPhone · Chrome, Firefox, Edge' },
    {
        cle: 'inapp',
        get libelle() {
            return t('sas.plateformes.inapp');
        },
    },
    { cle: 'android-chrome', libelle: 'Android · Chrome' },
    { cle: 'android-other', libelle: 'Android · Samsung Internet, Firefox' },
    {
        cle: 'desktop',
        get libelle() {
            return t('sas.plateformes.ordinateur');
        },
    },
];

/**
 * Les navigateurs embarqués dans une autre app.
 *
 * C'est là qu'atterrit un lien d'invitation ouvert depuis Gmail ou WhatsApp, et
 * aucun d'eux ne sait installer une app — d'où une consigne à part, qui ne parle
 * que d'en sortir. `FBAN`/`FBAV` (Facebook) et `Instagram` sont les seuls à se
 * nommer franchement ; `Line`, `WhatsApp` et `LinkedInApp` suivent la même forme.
 */
const MARQUEURS_IN_APP = /\b(FBAN|FBAV|FB_IAB|Instagram|Line|WhatsApp|LinkedInApp|Twitter|MicroMessenger)\b/i;

/**
 * Chrome et Firefox sur iOS s'annoncent par un suffixe qui leur est propre —
 * `CriOS`, `FxiOS`, `EdgiOS` — parce que le moteur en dessous reste WebKit. C'est
 * précisément ce qui rend la distinction utile : le moteur est le même, mais
 * l'action « Sur l'écran d'accueil » n'y est pas exposée pareil (#35).
 */
const MARQUEURS_IOS_TIERS = /\b(CriOS|FxiOS|EdgiOS|OPT|Chrome)\b/i;

export function detecterPlateforme(userAgent: string = navigator.userAgent): ClePlateforme {
    const ua = userAgent;

    // L'in-app d'abord : un navigateur embarqué DANS Gmail sur iPhone est un cas
    // d'in-app avant d'être un cas d'iPhone. La consigne « touchez Partager »
    // n'existe pas là-dedans, celle « ouvrez dans le navigateur » oui.
    if (MARQUEURS_IN_APP.test(ua)) return 'inapp';

    // L'iPad se déclare « Macintosh » depuis iPadOS 13 ; `maxTouchPoints` est le
    // seul discriminant qui reste, et c'est celui qu'Apple documente.
    const estIOS =
        /\b(iPhone|iPod|iPad)\b/.test(ua) ||
        (/\bMacintosh\b/.test(ua) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1);

    if (estIOS) return MARQUEURS_IOS_TIERS.test(ua) ? 'ios-other' : 'ios-safari';

    if (/\bAndroid\b/.test(ua)) {
        // `Chrome` sans `SamsungBrowser` ni `Firefox` : Samsung Internet et Edge
        // portent tous deux `Chrome` dans leur UA, d'où l'exclusion explicite.
        const estChrome = /\bChrome\//.test(ua) && !/\b(SamsungBrowser|EdgA|OPR|Firefox|FxiOS)\b/.test(ua);

        return estChrome ? 'android-chrome' : 'android-other';
    }

    return 'desktop';
}

/**
 * La version d'iOS, quand on peut la lire — #28, #80.
 *
 * Sert un seul point de la chaîne de diagnostic : **sous iOS 18.4, le mode
 * déclaratif n'existe pas**. Le service worker s'exécute alors pour afficher
 * chaque notification, et l'appareil hérite entièrement du risque de
 * désinscription silencieuse de #13 — WebKit coupe toute l'origine après trois
 * pushs sans affichage, sans erreur et sans trace.
 *
 * #28 a refusé de poser un plancher iOS dur : ces utilisateurs entrent quand même,
 * mais leur régime est plus fragile et on le leur DIT, avec le geste qui répare
 * (mettre à jour). Un état dégradé se répare depuis un écran, pas depuis un
 * silence.
 *
 * Mesure de #28 sur `login_histories` : **aucune connexion iOS sous 18.4** sur le
 * parc actuel. La poche est vide aujourd'hui — mais l'échantillon ne couvre que
 * les comptes existants, pas la population que le lot 1 va équiper, et un
 * iPhone 8 plafonne à iOS 16.7.
 *
 * `null` quand la version n'est pas lisible — un autre OS, ou une chaîne d'UA qui
 * ne la porte pas. On ne devine pas : afficher « votre iOS est trop ancien » à
 * quelqu'un sur Android serait pire que se taire.
 */
export function versionIOS(userAgent: string = navigator.userAgent): { majeure: number; mineure: number } | null {
    // `OS 18_4_1 like Mac OS X` — les séparateurs sont des underscores, et la
    // partie corrective est facultative.
    const trouve = /\bOS (\d+)_(\d+)/.exec(userAgent);
    if (!trouve) return null;

    return { majeure: Number(trouve[1]), mineure: Number(trouve[2]) };
}

/** La version à partir de laquelle le mode déclaratif existe (#28, #36). */
const IOS_DECLARATIF = { majeure: 18, mineure: 4 };

/**
 * Cet appareil est-il dans le régime dégradé ?
 *
 * `false` quand la version est illisible : on ne signale une fragilité que si on
 * l'a constatée. Un faux positif ici enverrait quelqu'un mettre à jour un
 * téléphone qui n'a aucun problème.
 */
export function regimeDegradeIOS(userAgent: string = navigator.userAgent): boolean {
    const version = versionIOS(userAgent);
    if (!version) return false;

    // Ne vaut que sur iOS : `OS x_y` n'apparaît pas dans un UA Android.
    const cle = detecterPlateforme(userAgent);
    if (cle !== 'ios-safari' && cle !== 'ios-other') return false;

    if (version.majeure !== IOS_DECLARATIF.majeure) return version.majeure < IOS_DECLARATIF.majeure;

    return version.mineure < IOS_DECLARATIF.mineure;
}
